// Keeps a batch of question documents reachable while offline.
//
// The pack is only a list of question IDs. The question bodies live in
// Firestore's own IndexedDB cache — reading a document once while online is
// what puts it there, so prefetching is just a fan of getDoc calls. Nothing is
// written to Firestore, and no field is added to users/{email}: a new field
// would leave every existing user document without it.
//
// THE PACK IS AN OFFLINE RESERVE AND NOTHING ELSE. Serving a round from it
// while ONLINE, to skip the network, looks like free latency and cannot work:
// getDoc sets waitForSyncWhenOnline, and QueryListener refuses to raise a
// fromCache snapshot unless onlineState is Offline. So an online getDoc waits
// for the server whether or not the document is already sitting in IndexedDB,
// and a warm cache buys nothing at all on the fast path.
//
// This was built and then reverted on a branch that never shipped. It is
// recorded here because it is an obvious idea to have twice, and because the
// code gives no hint that it fails — the reads succeed, they are simply not
// any faster.

import firebaseGetDocWrapper from "../wrappers/firebase/firebaseGetDocWrapper";
import shuffleArray from "./shuffleArray";
import { QUESTIONS } from "../../constants";

const PACK_SIZE = 30;

// Refill once the pack is nearly used up rather than every round. At 5
// questions a round that is roughly one refill per four rounds, so the cost is
// ~30 extra Firestore reads per four rounds, not per round.
const REFILL_BELOW = 10;

function key(email) {
  return `tgmq.offlinePack.${email}`;
}

export function getPack(email) {
  try {
    const stored = JSON.parse(localStorage.getItem(key(email)));
    return Array.isArray(stored) ? stored : [];
  } catch {
    // Unset, malformed, or localStorage unavailable (Safari with cookies
    // blocked throws on read). No pack means no offline play, not a crash.
    return [];
  }
}

// Called on sign-out, alongside clearing Firestore's IndexedDB cache, so the
// next person on a shared browser inherits nothing.
export function clearPack(email) {
  if (!email) return;
  try {
    localStorage.removeItem(key(email));
  } catch {
    // localStorage unavailable; there was nothing stored to clear.
  }
}

// Drop ids whose documents could not be loaded. Without this, an id that is not
// really in the cache stays in the pack and Play Again keeps redrawing it while
// playable questions sit unused.
export function prunePack(email, failedIDs) {
  if (failedIDs.length === 0) return;
  savePack(
    email,
    getPack(email).filter(id => !failedIDs.includes(id))
  );
}

function savePack(email, ids) {
  try {
    localStorage.setItem(key(email), JSON.stringify(ids));
  } catch {
    // Private browsing. Offline play is unavailable; online play is unaffected.
  }
}

// Fire-and-forget. Callers must not await this: it runs in the background
// after a round has already rendered, and a failure must not block play.
//
// ponytail: the pack is drawn from the whole pool, so a brand-new player who
// goes offline gets random questions rather than the on-ramp's starter set
// (see starterQuestionIDs.js). Narrowing it would mean threading totalCorrect
// in here, for the case of a first-week player who is also offline. Left alone
// until that case is worth a signature change.
export async function refillPack(db, email, unansweredQnsIDs) {
  const stillUsable = getPack(email).filter(id =>
    unansweredQnsIDs.includes(id)
  );
  if (stillUsable.length >= REFILL_BELOW) return;

  const ids = shuffleArray(unansweredQnsIDs).slice(0, PACK_SIZE);

  // Reading each document is the point: it populates persistentLocalCache.
  const fetched = await Promise.all(
    ids.map(id =>
      firebaseGetDocWrapper(db, QUESTIONS, id)
        .then(question => (question ? id : null))
        .catch(() => null)
    )
  );

  // Merge rather than replace. If the connection drops part-way through, every
  // read returns null, and overwriting would wipe a pack whose questions are
  // still sitting in the cache — stranding the user with playable questions the
  // app then refuses to serve.
  const merged = new Set([...stillUsable, ...fetched.filter(Boolean)]);
  savePack(email, [...merged]);
}
